import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.salonvora.com/api'

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  withCredentials: false,
})

// ── Request interceptor: attach token ──
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sv_token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  (error) => Promise.reject(error)
)

// ── Response interceptor: handle 401 ──
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sv_token')
      localStorage.removeItem('sv_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api

// ── Auth ──
export const authAPI = {
  login:          (data) => api.post('/auth/login', data),
  register:       (data) => api.post('/auth/register', data),
  logout:         ()     => api.post('/auth/logout'),
  me:             ()     => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword:  (data) => api.post('/auth/reset-password', data),
  updateProfile:  (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
}

// ── Services ──
export const servicesAPI = {
  getAll:       (params) => api.get('/services', { params }),
  getById:      (id)     => api.get(`/services/${id}`),
  getBySlug:    (slug)   => api.get(`/services/slug/${slug}`),
  getCategories:()       => api.get('/service-categories'),
  create:       (data)   => api.post('/services', data),
  update:       (id, d)  => api.put(`/services/${id}`, d),
  delete:       (id)     => api.delete(`/services/${id}`),
}

// ── Staff ──
export const staffAPI = {
  getAll:       (params) => api.get('/staff', { params }),
  getById:      (id)     => api.get(`/staff/${id}`),
  getAvailable: (params) => api.get('/staff/available', { params }),
  create:       (data)   => api.post('/staff', data),
  update:       (id, d)  => api.put(`/staff/${id}`, d),
  delete:       (id)     => api.delete(`/staff/${id}`),
}

// ── Appointments ──
export const appointmentsAPI = {
  getAll:       (params) => api.get('/appointments', { params }),
  getById:      (id)     => api.get(`/appointments/${id}`),
  getMine:      (params) => api.get('/appointments/my', { params }),
  create:       (data)   => api.post('/appointments', data),
  update:       (id, d)  => api.put(`/appointments/${id}`, d),
  cancel:       (id)     => api.put(`/appointments/${id}/cancel`),
  reschedule:   (id, d)  => api.put(`/appointments/${id}/reschedule`, d),
  getSlots:     (params) => api.get('/appointments/slots', { params }),
}

// ── Payments ──
export const paymentsAPI = {
  createIntent: (data)   => api.post('/payments/intent', data),
  confirm:      (data)   => api.post('/payments/confirm', data),
  getHistory:   (params) => api.get('/payments', { params }),
}

// ── Reviews ──
export const reviewsAPI = {
  getAll:       (params) => api.get('/reviews', { params }),
  create:       (data)   => api.post('/reviews', data),
  delete:       (id)     => api.delete(`/reviews/${id}`),
}

// ── Dashboard (admin) ──
export const dashboardAPI = {
  getStats:          () => api.get('/dashboard/stats'),
  getRevenue:        (params) => api.get('/dashboard/revenue-chart', { params }),
  getServiceStats:   (params) => api.get('/dashboard/service-stats', { params }),
  getMonthlyBookings:(params) => api.get('/dashboard/monthly-bookings', { params }),
  getRecent:         () => api.get('/dashboard/recent'),
}

// ── Coupons ──
export const couponsAPI = {
  validate:     (code) => api.post('/coupons/validate', { code }),
  getAll:       ()     => api.get('/coupons'),
  create:       (data) => api.post('/coupons', data),
  update:       (id,d) => api.put(`/coupons/${id}`, d),
  delete:       (id)   => api.delete(`/coupons/${id}`),
}

// ── Settings (admin) ──
export const settingsAPI = {
  get:    () => api.get('/settings'),
  update: (data) => api.post('/settings', data),
}

// ── Contact ──
export const contactAPI = {
  send: (data) => api.post('/contact', data),
}

// ── CSV import (admin) ──
export const importAPI = {
  services: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/services/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  staff: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/staff/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}
